<template>
  <!-- Frosted Glass Navbar -->
  <nav class="fixed top-0 left-0 right-0 z-50 bg-gray-900/70 backdrop-blur-xl border-b border-white/5">
    <div class="container mx-auto px-6 py-4 flex justify-between items-center">
      <div class="text-xl font-bold tracking-tight">
        <span class="text-white">BetterConnect</span>
      </div>
      <button
        @click="openSchoolChooser"
        class="relative px-5 py-2 rounded-lg bg-white/5 border border-white/10 text-sm font-medium text-white hover:bg-white/10 hover:border-violet-500/50 hover:shadow-[0_0_20px_rgba(139,92,246,0.3)] transition-all duration-300"
      >
        Login
      </button>
    </div>
  </nav>

  <div class="min-h-screen bg-gray-950 text-white flex flex-col pt-16">

    <!-- Hero Section -->
    <header class="relative container mx-auto px-6 py-20 md:py-32 overflow-hidden">
      <!-- Ambient glow -->
      <div class="absolute top-1/2 left-1/4 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-violet-600/10 rounded-full blur-[120px] pointer-events-none"></div>
      <div class="absolute top-1/3 right-1/4 w-[400px] h-[400px] bg-cyan-600/10 rounded-full blur-[100px] pointer-events-none"></div>

      <div class="relative flex flex-col md:flex-row items-center gap-12 md:gap-16">
        <!-- Left side: Copy -->
        <div class="flex-1 text-center md:text-left">
          <h1 class="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6">
            <span class="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 via-purple-400 to-cyan-400 gradient-text-animated">Stop Logging Into</span>
            <br>
            <span class="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 via-purple-400 to-cyan-400 gradient-text-animated">Connect Every Day.</span>
          </h1>
          <p class="text-lg md:text-xl text-gray-400 mb-8 max-w-lg mx-auto md:mx-0">
            Your assignments, schedule, and notifications &mdash; all in one place. One login, zero hassle.
          </p>
          <div class="flex flex-col gap-4 items-center md:items-start">
            <button
              @click="openSchoolChooser"
              class="group relative px-8 py-4 rounded-2xl bg-gradient-to-r from-violet-600 to-purple-600 text-white font-semibold text-lg shadow-lg transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_0_30px_rgba(139,92,246,0.4)]"
            >
              Get Started
              <span class="inline-block ml-1 transition-transform duration-200 group-hover:translate-x-1">&rarr;</span>
            </button>
            <div class="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-gray-400">
              <span class="inline-block w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
              <span class="tabular-nums font-medium text-white">{{ displayCount }}</span> students joined
            </div>
          </div>
        </div>

        <!-- Right side: Floating demo cards -->
        <div class="flex-1 relative min-h-[400px] hidden md:block">
          <!-- Assignment Card -->
          <div
            class="demo-card absolute top-0 right-0 w-72 bg-gray-900/80 backdrop-blur-sm border border-white/10 rounded-2xl p-5 shadow-2xl rotate-2 hover:rotate-0 transition-all duration-500 hover:scale-105 hover:border-violet-500/40 hover:shadow-[0_0_30px_rgba(139,92,246,0.15)] scroll-hidden stagger-1"
          >
            <div class="flex items-center gap-2 mb-3">
              <div class="w-8 h-8 rounded-lg bg-violet-500/20 flex items-center justify-center">
                <svg class="w-4 h-4 text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"></path></svg>
              </div>
              <span class="text-xs font-medium text-gray-400 uppercase tracking-wider">Assignment</span>
            </div>
            <p class="font-semibold text-white mb-1">Biology: Homework</p>
            <p class="text-sm text-gray-400 mb-3">Due Aug 25, 2024</p>
            <div class="w-full bg-gray-700/50 rounded-full h-1.5 mb-1">
              <div class="bg-gradient-to-r from-green-400 to-emerald-500 h-1.5 rounded-full" style="width: 75%"></div>
            </div>
            <p class="text-xs text-gray-500">15 of 20 completed</p>
          </div>

          <!-- Schedule Card -->
          <div
            class="demo-card absolute top-24 left-0 w-64 bg-gray-900/80 backdrop-blur-sm border border-white/10 rounded-2xl p-5 shadow-2xl -rotate-2 hover:rotate-0 transition-all duration-500 hover:scale-105 hover:border-cyan-500/40 hover:shadow-[0_0_30px_rgba(6,182,212,0.15)] scroll-hidden stagger-2"
          >
            <div class="flex items-center gap-2 mb-3">
              <div class="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center">
                <svg class="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
              </div>
              <span class="text-xs font-medium text-gray-400 uppercase tracking-wider">Today's Schedule</span>
            </div>
            <div class="space-y-2">
              <div class="flex justify-between text-sm"><span class="text-white">Biology</span><span class="text-gray-500">8:00 AM</span></div>
              <div class="flex justify-between text-sm"><span class="text-white">French</span><span class="text-gray-500">9:15 AM</span></div>
              <div class="flex justify-between text-sm"><span class="text-white">English</span><span class="text-gray-500">10:30 AM</span></div>
            </div>
          </div>

          <!-- Menu Card -->
          <div
            class="demo-card absolute bottom-0 right-8 w-72 bg-gray-900/80 backdrop-blur-sm border border-white/10 rounded-2xl p-5 shadow-2xl rotate-1 hover:rotate-0 transition-all duration-500 hover:scale-105 hover:border-rose-500/40 hover:shadow-[0_0_30px_rgba(244,63,94,0.15)] scroll-hidden stagger-3"
          >
            <div class="flex items-center gap-2 mb-3">
              <div class="w-8 h-8 rounded-lg bg-rose-500/20 flex items-center justify-center">
                <svg class="w-4 h-4 text-rose-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"></path></svg>
              </div>
              <span class="text-xs font-medium text-gray-400 uppercase tracking-wider">Today's Menu</span>
            </div>
            <div class="space-y-2">
              <div class="flex justify-between text-sm"><span class="text-white">Chicken Parmesan</span><span class="text-gray-500">Lunch</span></div>
              <div class="flex justify-between text-sm"><span class="text-white">Caesar Salad</span><span class="text-gray-500">Side</span></div>
              <div class="flex justify-between text-sm"><span class="text-white">Fresh Fruit</span><span class="text-gray-500">Dessert</span></div>
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- Stats Section -->
    <section ref="statsSection" class="container mx-auto py-16 px-6">
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div class="scroll-hidden stagger-1 bg-gray-900/60 backdrop-blur-sm border border-white/5 rounded-2xl p-8 text-center glow-border-cyan group">
          <div class="w-12 h-12 rounded-xl bg-cyan-500/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-cyan-500/20 transition-colors">
            <svg class="w-6 h-6 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path></svg>
          </div>
          <div class="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-500 mb-2 tabular-nums">
            {{ displayAssignmentCount }}+
          </div>
          <div class="text-gray-400 text-sm">Assignments Tracked</div>
        </div>

        <div class="scroll-hidden stagger-2 bg-gray-900/60 backdrop-blur-sm border border-white/5 rounded-2xl p-8 text-center glow-border-violet group">
          <div class="w-12 h-12 rounded-xl bg-green-500/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-green-500/20 transition-colors">
            <svg class="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg>
          </div>
          <div class="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-500 mb-2">
            99.9%
          </div>
          <div class="text-gray-400 text-sm">Uptime Reliability</div>
        </div>

        <div class="scroll-hidden stagger-3 bg-gray-900/60 backdrop-blur-sm border border-white/5 rounded-2xl p-8 text-center glow-border-rose group">
          <div class="w-12 h-12 rounded-xl bg-rose-500/10 flex items-center justify-center mx-auto mb-4 group-hover:bg-rose-500/20 transition-colors">
            <svg class="w-6 h-6 text-rose-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path></svg>
          </div>
          <div class="text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-rose-400 to-pink-500 mb-2">
            &lt;1s
          </div>
          <div class="text-gray-400 text-sm">Average Load Time</div>
        </div>
      </div>
    </section>

    <!-- Feature Preview Section -->
    <section class="container mx-auto py-16 px-6">
      <div class="text-center mb-12 scroll-hidden">
        <h2 class="text-3xl md:text-5xl font-bold mb-4">
          <span class="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 to-cyan-400">See It In Action</span>
        </h2>
        <p class="text-gray-400 text-lg max-w-xl mx-auto">
          Everything you need to stay on top of your coursework
        </p>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
        <!-- Upcoming Assignments Preview -->
        <div class="scroll-hidden stagger-1 bg-gray-900/60 backdrop-blur-sm border border-white/5 rounded-2xl p-8 glow-border-violet">
          <div class="flex items-center mb-6">
            <div class="w-10 h-10 rounded-xl bg-violet-500/10 flex items-center justify-center mr-3">
              <svg class="w-5 h-5 text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path>
              </svg>
            </div>
            <h3 class="text-2xl font-semibold">Upcoming Assignments</h3>
          </div>
          <ul>
            <li
              v-for="assignment in assignments"
              :key="assignment.id"
              class="border-b border-white/5 py-4 last:border-b-0 cursor-pointer hover:bg-white/[0.02] rounded-lg transition-colors duration-300 px-3"
              @click="openModal(assignment)"
            >
              <div class="flex justify-between items-center">
                <div>
                  <span class="font-medium text-white">{{ assignment.section }}: </span>
                  <span class="text-gray-300">{{ assignment.title }}</span>
                </div>
                <span class="text-gray-500 text-sm">{{ assignment.dueDate }}</span>
              </div>
              <div class="mt-3">
                <div class="w-full bg-gray-800/80 rounded-full h-1.5">
                  <div
                    class="bg-gradient-to-r from-green-400 to-emerald-500 h-1.5 rounded-full transition-all duration-500"
                    :style="{ width: (assignment.completedBy / assignment.totalStudents * 100) + '%' }"
                  ></div>
                </div>
                <p class="text-xs text-gray-500 mt-1.5">
                  {{ assignment.completedBy }} of {{ assignment.totalStudents }} students completed
                </p>
              </div>
            </li>
          </ul>
        </div>

        <!-- Daily Schedule Preview -->
        <div class="scroll-hidden stagger-2 bg-gray-900/60 backdrop-blur-sm border border-white/5 rounded-2xl p-8 glow-border-cyan">
          <div class="flex items-center mb-6">
            <div class="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center mr-3">
              <svg class="w-5 h-5 text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
              </svg>
            </div>
            <h3 class="text-2xl font-semibold">Today's Schedule</h3>
          </div>
          <div class="overflow-x-auto">
            <table class="min-w-full table-auto">
              <thead>
                <tr class="border-b border-white/5">
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Time</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Class</th>
                  <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Room</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="schedule in schedules" :key="schedule.id" class="border-b border-white/5 last:border-b-0 hover:bg-white/[0.02] transition-colors">
                  <td class="px-4 py-3.5 text-sm text-gray-400">{{ schedule.time }}</td>
                  <td class="px-4 py-3.5 font-medium text-white">{{ schedule.class }}</td>
                  <td class="px-4 py-3.5 text-sm text-gray-500">{{ schedule.room }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="container mx-auto py-16 px-6">
      <div class="scroll-hidden relative grain-overlay rounded-3xl p-12 md:p-16 text-center overflow-hidden" style="background: radial-gradient(ellipse at 20% 50%, rgba(139,92,246,0.3) 0%, transparent 50%), radial-gradient(ellipse at 80% 50%, rgba(6,182,212,0.2) 0%, transparent 50%), radial-gradient(ellipse at 50% 100%, rgba(244,63,94,0.15) 0%, transparent 50%), linear-gradient(to bottom right, #1e1b4b, #0f172a);">
        <h2 class="text-3xl md:text-5xl font-bold mb-4 text-white">
          Ready to Take Control?
        </h2>
        <p class="text-lg text-gray-300 mb-8 max-w-lg mx-auto">
          Join hundreds of students who are already managing their assignments smarter
        </p>
        <button
          @click="openSchoolChooser"
          class="relative px-8 py-4 rounded-full bg-white text-gray-900 font-semibold text-lg shadow-lg hover:shadow-[0_0_30px_rgba(255,255,255,0.3)] transform hover:scale-105 transition-all duration-300"
        >
          Start Using BetterConnect
        </button>
      </div>
    </section>

    <!-- School Chooser Overlay -->
    <Transition name="chooser">
      <div v-if="showSchoolChooser" class="fixed inset-0 z-[60] flex items-center justify-center chooser-backdrop" @click.self="closeSchoolChooser">
        <!-- Ambient blobs -->
        <div class="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-violet-600/20 rounded-full blur-[150px] pointer-events-none"></div>
        <div class="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-cyan-600/15 rounded-full blur-[150px] pointer-events-none"></div>

        <div class="relative w-full max-w-3xl mx-4">
          <!-- Close button -->
          <button @click="closeSchoolChooser" class="absolute -top-12 right-0 text-white/50 hover:text-white transition-colors text-sm flex items-center gap-1.5">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>
            Close
          </button>

          <!-- Heading -->
          <div class="text-center mb-8">
            <h2 class="text-2xl md:text-3xl font-bold text-white mb-2">Choose your school</h2>
            <p class="text-gray-400 text-sm">Select your school to sign in with Blackbaud</p>
            <p v-if="loginError === 'wrong_school'" class="mt-4 text-sm text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded-xl px-4 py-3 inline-block">
              Looks like you picked the wrong school. Please try again.
            </p>
          </div>

          <!-- School Cards -->
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <button
              v-for="school in schoolOptions"
              :key="school.id"
              @click="selectSchoolAndLogin(school.id)"
              class="school-card group relative overflow-hidden rounded-2xl border border-white/10 bg-gray-900/80 backdrop-blur-sm p-8 text-left transition-all duration-300 hover:scale-[1.02] hover:border-violet-500/50 hover:shadow-[0_0_40px_rgba(139,92,246,0.2)] focus:outline-none focus:border-violet-500/50"
            >
              <!-- Background shimmer on hover -->
              <div class="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style="background: radial-gradient(ellipse at 50% 0%, rgba(139,92,246,0.08) 0%, transparent 70%)"></div>

              <div class="relative">
                <!-- School logo -->
                <div class="mb-5 flex items-center">
                  <img v-if="school.id === 'collegiate'" src="/CS_Crest_Blue_PMS.png" alt="Collegiate School" class="h-14 object-contain" />
                  <img v-else src="/GraceCrest-TextRight-294U.png" alt="Grace Church School" class="h-10 object-contain" />
                </div>

                <h3 class="text-xl font-semibold text-white mb-1.5">{{ school.name }}</h3>
                <p class="text-sm text-gray-400 mb-5">Sign in with your {{ school.name }} account</p>

                <!-- Arrow indicator -->
                <div class="flex items-center gap-2 text-sm font-medium"
                  :class="school.id === 'collegiate' ? 'text-violet-400' : 'text-cyan-400'"
                >
                  Continue
                  <svg class="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path></svg>
                </div>
              </div>
            </button>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Modal for Assignment Description -->
    <Transition name="modal">
      <div v-if="showModal" class="fixed inset-0 flex items-center justify-center z-50 bg-black/60 backdrop-blur-sm" @click.self="closeModal">
        <div class="bg-gray-900 border border-white/10 rounded-2xl shadow-2xl p-8 max-w-lg w-full mx-4">
          <h3 class="text-2xl font-semibold mb-4 text-white">{{ currentAssignment.section }}: {{ currentAssignment.title }}</h3>
          <p class="text-gray-400 mb-6">{{ currentAssignment.description }}</p>
          <button @click="closeModal" class="bg-violet-600 hover:bg-violet-700 text-white px-6 py-3 rounded-xl transition-colors w-full font-medium">
            Close
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<script>
import { redirectToLogin as beginLogin, resolveLoginRedirectTarget } from '@/config/auth';
import { resolveSchoolId, schoolOptions, syncActiveSchool } from '@/config/schools';

export default {
  computed: {
    selectedSchoolName() {
      return this.schoolOptions.find((school) => school.id === this.selectedSchoolId)?.name || '';
    },
  },
  data() {
    return {
      showModal: false,
      showSchoolChooser: false,
      currentAssignment: null,
      displayCount: 0,
      displayAssignmentCount: 0,
      targetCount: 401,
      targetAssignmentCount: 2500,
      countersAnimated: false,
      schoolOptions,
      selectedSchoolId: resolveSchoolId({ fallbackToDefault: false }) || '',
      showSchoolError: false,
      loginError: null,
      assignments: [
        {
          id: 1,
          section: 'Biology',
          title: 'Homework',
          dueDate: 'Aug 25, 2024',
          description: 'Complete the worksheet on photosynthesis and submit it by the due date.',
          completedBy: 15,
          totalStudents: 20,
        },
        {
          id: 2,
          section: 'French',
          title: 'Essay',
          dueDate: 'Aug 27, 2024',
          description: 'Write a 500-word essay on the influence of French culture in the modern world.',
          completedBy: 10,
          totalStudents: 20,
        },
        {
          id: 3,
          section: 'Math',
          title: 'Geometry Quiz',
          dueDate: 'Aug 30, 2024',
          description: 'Study for the upcoming geometry quiz covering chapters 4 and 5.',
          completedBy: 5,
          totalStudents: 20,
        },
      ],
      schedules: [
        { id: 1, time: '8:00 AM - 9:00 AM', class: 'Biology', room: 'US Science 708' },
        { id: 2, time: '9:15 AM - 10:15 AM', class: 'French', room: 'US Classroom 501' },
        { id: 3, time: '10:30 AM - 11:30 AM', class: 'English', room: 'US Classroom 613' },
        { id: 4, time: '12:30 PM - 1:30 PM', class: 'Geometry', room: 'US Classroom 606' },
        { id: 5, time: '1:45 PM - 2:45 PM', class: 'Programming', room: 'US Science 712' },
        { id: 6, time: '3:00 PM - 4:00 PM', class: 'Latin', room: 'US Classroom 611' },
      ],
    };
  },
  mounted() {
    if (this.selectedSchoolId) {
      syncActiveSchool({ schoolId: this.selectedSchoolId });
    }

    // Check for wrong school redirect
    const loginError = this.$route.query.loginError;
    if (loginError === 'wrong_school') {
      this.loginError = 'wrong_school';
      this.showSchoolChooser = true;
      // Clean the URL
      const cleanUrl = new URL(window.location.href);
      cleanUrl.searchParams.delete('loginError');
      window.history.replaceState({}, document.title, cleanUrl.pathname + cleanUrl.search);
    }

    this.animateCounter();
    this.setupScrollObserver();
  },
  beforeUnmount() {
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    setupScrollObserver() {
      const scrollElements = document.querySelectorAll('.scroll-hidden');

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('scroll-visible');

            // Trigger counter animations when stats section enters view
            if (this.$refs.statsSection && this.$refs.statsSection.contains(entry.target) && !this.countersAnimated) {
              this.countersAnimated = true;
              this.animateAssignmentCounter();
            }
          }
        });
      }, {
        threshold: 0.1,
        rootMargin: '0px 0px -50px 0px',
      });

      scrollElements.forEach((el) => this.observer.observe(el));
    },
    openSchoolChooser() {
      this.showSchoolChooser = true;
    },
    closeSchoolChooser() {
      this.showSchoolChooser = false;
    },
    selectSchoolAndLogin(schoolId) {
      this.selectedSchoolId = schoolId;
      this.loginError = null;
      syncActiveSchool({ schoolId });
      this.showSchoolChooser = false;
      beginLogin({
        originalUrl: this.getLoginRedirectTarget(),
        schoolId,
      });
    },
    handleSchoolChange() {
      syncActiveSchool({ schoolId: this.selectedSchoolId });
      this.showSchoolError = false;
    },
    getLoginRedirectTarget() {
      return resolveLoginRedirectTarget(this.$route.query.loginRedirect);
    },
    animateCounter() {
      const duration = 1500;
      const steps = 60;
      const increment = this.targetCount / steps;
      const stepDuration = duration / steps;

      let currentStep = 0;
      const timer = setInterval(() => {
        currentStep++;
        this.displayCount = Math.min(
          Math.floor(increment * currentStep),
          this.targetCount
        );

        if (currentStep >= steps) {
          clearInterval(timer);
          this.displayCount = this.targetCount;
        }
      }, stepDuration);
    },
    animateAssignmentCounter() {
      const duration = 2000;
      const steps = 60;
      const increment = this.targetAssignmentCount / steps;
      const stepDuration = duration / steps;

      let currentStep = 0;
      const timer = setInterval(() => {
        currentStep++;
        this.displayAssignmentCount = Math.min(
          Math.floor(increment * currentStep),
          this.targetAssignmentCount
        );

        if (currentStep >= steps) {
          clearInterval(timer);
          this.displayAssignmentCount = this.targetAssignmentCount;
        }
      }, stepDuration);
    },
    redirectToLogin() {
      if (!this.selectedSchoolId) {
        this.openSchoolChooser();
        return;
      }

      syncActiveSchool({ schoolId: this.selectedSchoolId });
      beginLogin({
        originalUrl: this.getLoginRedirectTarget(),
        schoolId: this.selectedSchoolId,
      });
    },
    openModal(assignment) {
      this.currentAssignment = assignment;
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
      this.currentAssignment = null;
    },
  },
};
</script>

<style scoped>
.tabular-nums {
  font-variant-numeric: tabular-nums;
}

/* School chooser overlay */
.chooser-backdrop {
  background: rgba(3, 7, 18, 0.92);
  backdrop-filter: blur(20px);
}

.chooser-enter-active {
  transition: opacity 0.3s ease;
}
.chooser-enter-active .school-card {
  transition: transform 0.4s cubic-bezier(0.16, 1, 0.3, 1), opacity 0.4s ease;
}
.chooser-enter-active .school-card:nth-child(2) {
  transition-delay: 0.08s;
}
.chooser-leave-active {
  transition: opacity 0.2s ease;
}
.chooser-enter-from {
  opacity: 0;
}
.chooser-enter-from .school-card {
  opacity: 0;
  transform: translateY(20px) scale(0.97);
}
.chooser-leave-to {
  opacity: 0;
}

/* Modal transition */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.25s ease;
}
.modal-enter-active .bg-gray-900,
.modal-leave-active .bg-gray-900 {
  transition: transform 0.25s ease, opacity 0.25s ease;
}
.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}
.modal-enter-from .bg-gray-900 {
  transform: scale(0.95);
  opacity: 0;
}
.modal-leave-to .bg-gray-900 {
  transform: scale(0.95);
  opacity: 0;
}
</style>
<script setup lang="ts">
</script>
